import React, { useState, useEffect } from 'react';
import { ArrowRight, CheckCircle, Globe, Users, Zap, Building, Factory, Heart, Truck, Shield, Eye, Star, ArrowDown, Menu, X, ExternalLink } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { mockData } from '../data/mock';

const LandingPage = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setIsVisible(true);
  }, []);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
    setIsMenuOpen(false);
  };

  return (
    <div className="min-h-screen bg-white text-black">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 bg-white/90 backdrop-blur-md border-b border-gray-100 z-50">
        <div className="max-w-7xl mx-auto px-6 py-5 flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <img 
              src="https://customer-assets.emergentagent.com/job_smart-ai-services-1/artifacts/7lqjkekm_mahoga_logo.jpg" 
              alt="Mahoga.ai Logo" 
              className="h-14 w-auto"
            />
          </div>
          
          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-8">
            <button onClick={() => scrollToSection('home')} className="text-sm font-normal hover:text-gray-600 transition-colors">
              Home
            </button>
            <button onClick={() => scrollToSection('services')} className="text-sm font-normal hover:text-gray-600 transition-colors">
              Solutions
            </button>
            <button onClick={() => scrollToSection('industries')} className="text-sm font-normal hover:text-gray-600 transition-colors">
              Industries
            </button>
            <button onClick={() => scrollToSection('approach')} className="text-sm font-normal hover:text-gray-600 transition-colors">
              Approach
            </button>
            <button onClick={() => scrollToSection('impact')} className="text-sm font-normal hover:text-gray-600 transition-colors">
              Impact
            </button>
            <button onClick={() => scrollToSection('contact')} className="text-sm font-normal hover:text-gray-600 transition-colors">
              Contact
            </button>
          </nav>

          {/* Mobile Menu Button */}
          <button 
            className="md:hidden"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
          >
            {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Navigation */}
        {isMenuOpen && (
          <div className="md:hidden bg-white border-t border-gray-100">
            <nav className="flex flex-col p-6 space-y-4">
              <button onClick={() => scrollToSection('home')} className="text-left text-sm font-normal hover:text-gray-600 transition-colors">
                Home
              </button>
              <button onClick={() => scrollToSection('services')} className="text-left text-sm font-normal hover:text-gray-600 transition-colors">
                Solutions
              </button>
              <button onClick={() => scrollToSection('industries')} className="text-left text-sm font-normal hover:text-gray-600 transition-colors">
                Industries
              </button>
              <button onClick={() => scrollToSection('approach')} className="text-left text-sm font-normal hover:text-gray-600 transition-colors">
                Approach
              </button>
              <button onClick={() => scrollToSection('impact')} className="text-left text-sm font-normal hover:text-gray-600 transition-colors">
                Impact
              </button>
              <button onClick={() => scrollToSection('contact')} className="text-left text-sm font-normal hover:text-gray-600 transition-colors">
                Contact
              </button>
            </nav>
          </div>
        )}
      </header>

      {/* Hero Section */}
      <section className="pt-28 pb-16 md:pt-36 md:pb-24 px-6" id="home">
        <div className="max-w-7xl mx-auto">
          <div className="max-w-4xl mx-auto text-center">
            <div className={`transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
              <h1 className="text-4xl md:text-6xl lg:text-7xl font-light leading-tight tracking-tight mb-6">
                SAP + AI →<br />
                <span className="font-normal">Smarter Business</span>
              </h1>
              <p className="text-lg md:text-xl text-gray-600 max-w-3xl mx-auto mb-8 leading-relaxed">
                {mockData.hero.description}
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                <Button 
                  size="lg" 
                  className="bg-black text-white hover:bg-gray-800 px-8 py-6 text-base"
                  onClick={() => scrollToSection('contact')}
                >
                  Start Your Transformation
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
                <Button 
                  variant="outline" 
                  size="lg" 
                  className="border-gray-300 hover:bg-gray-50 px-8 py-6 text-base"
                  onClick={() => scrollToSection('services')}
                >
                  Explore Solutions
                </Button>
              </div>
            </div>
            
            {/* Animated Scroll Indicator */}
            <div className="mt-16 flex justify-center">
              <div className="animate-bounce">
                <ArrowDown className="w-6 h-6 text-gray-400" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Section */}
      <section className="py-16 md:py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="max-w-4xl mx-auto text-center mb-16">
            <h2 className="text-3xl md:text-5xl font-light mb-6">
              More than a consultancy —<br />
              <span className="font-normal">a partner in intelligent transformation</span>
            </h2>
            <p className="text-lg text-gray-600 leading-relaxed">
              {mockData.why.description}
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {mockData.why.differentiators.map((item, index) => (
              <Card key={index} className="bg-white border-0 shadow-sm hover:shadow-md transition-shadow duration-300">
                <CardContent className="p-8">
                  <div className="mb-4">
                    <CheckCircle className="w-6 h-6 text-black" />
                  </div>
                  <h3 className="text-xl font-medium mb-3">{item.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{item.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Solutions Section */}
      <section id="services" className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-6">
          <div className="max-w-4xl mx-auto text-center mb-16">
            <h2 className="text-3xl md:text-5xl font-light mb-6">
              Integrated. Intelligent.<br />
              <span className="font-normal">Enterprise-Grade.</span>
            </h2>
            <p className="text-lg text-gray-600 leading-relaxed">
              {mockData.solutions.description}
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-8">
            {mockData.solutions.services.map((service, index) => (
              <Card key={index} className="border-0 shadow-sm hover:shadow-lg transition-all duration-300 hover:scale-[1.02]">
                <CardContent className="p-8">
                  <div className="mb-6">
                    <service.icon className="w-8 h-8 text-black" />
                  </div>
                  <h3 className="text-2xl font-light mb-4">{service.title}</h3>
                  <p className="text-gray-600 mb-6 leading-relaxed">{service.description}</p>
                  <div className="grid grid-cols-2 gap-3">
                    {service.features.map((feature, idx) => (
                      <div key={idx} className="flex items-center space-x-2">
                        <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0" />
                        <span className="text-sm text-gray-700">{feature}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Industries Section */}
      <section id="industries" className="py-16 md:py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="max-w-4xl mx-auto text-center mb-16">
            <h2 className="text-3xl md:text-5xl font-light mb-6">
              Built for Your World.<br />
              <span className="font-normal">Engineered for Your Needs.</span>
            </h2>
            <p className="text-lg text-gray-600 leading-relaxed">
              {mockData.industries.description}
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {mockData.industries.sectors.map((industry, index) => (
              <Card key={index} className="bg-white border-0 shadow-sm hover:shadow-md transition-all duration-300 group">
                <CardContent className="p-6">
                  <div className="flex items-center space-x-4 mb-4">
                    <div className="p-2 bg-gray-100 rounded-lg group-hover:bg-black group-hover:text-white transition-colors">
                      <industry.icon className="w-6 h-6" />
                    </div>
                    <h3 className="text-lg font-medium">{industry.name}</h3>
                  </div>
                  <p className="text-gray-600 text-sm leading-relaxed">{industry.value}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Approach Section */}
      <section id="approach" className="py-16 md:py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="max-w-4xl mx-auto text-center mb-16">
            <h2 className="text-3xl md:text-5xl font-light mb-6">
              Agile by Design.<br />
              <span className="font-normal">Outcome by Default.</span>
            </h2>
            <p className="text-lg text-gray-600 leading-relaxed">
              {mockData.approach.description}
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-8">
            {mockData.approach.phases.map((phase, index) => (
              <div key={index} className="text-center">
                <div className="mb-6">
                  <div className="w-12 h-12 bg-black text-white rounded-full flex items-center justify-center mx-auto mb-4 text-lg font-medium">
                    {index + 1}
                  </div>
                  <h3 className="text-lg font-medium mb-3 text-black">{phase.phase}</h3>
                  <p className="text-gray-600 text-sm leading-relaxed">{phase.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Client Impact Section */}
      <section id="impact" className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-6">
          <div className="max-w-4xl mx-auto text-center mb-16">
            <h2 className="text-3xl md:text-5xl font-light mb-6">
              Measurable Results,<br />
              <span className="font-normal">Not Just Technology.</span>
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {mockData.impact.metrics.map((metric, index) => (
              <div key={index} className="text-center">
                <div className="text-4xl md:text-5xl font-light text-black mb-2">{metric.value}</div>
                <p className="text-gray-600">{metric.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Global Delivery Section */}
      <section className="py-16 md:py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="max-w-4xl mx-auto text-center mb-16">
            <h2 className="text-3xl md:text-5xl font-light mb-6">
              Think Big.<br />
              <span className="font-normal">Execute Anywhere.</span>
            </h2>
            <p className="text-lg text-gray-600 leading-relaxed mb-8">
              {mockData.global.description}
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {mockData.global.capabilities.map((capability, index) => (
              <div key={index} className="text-center">
                <div className="mb-4">
                  <capability.icon className="w-8 h-8 text-black mx-auto" />
                </div>
                <h3 className="text-lg font-medium mb-2">{capability.title}</h3>
                <p className="text-gray-600 text-sm">{capability.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-16 md:py-24">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-3xl md:text-5xl font-light mb-6">
            Let's Start the<br />
            <span className="font-normal">Conversation</span>
          </h2>
          <p className="text-lg text-gray-600 mb-8 leading-relaxed max-w-2xl mx-auto">
            {mockData.contact.description}
          </p>
          
          <div className="flex flex-col sm:flex-row gap-6 justify-center items-center text-sm text-gray-600 mb-8">
            <span>📧 info@mahoga.ai</span>
            <span>🌍 www.mahoga.ai</span>
            <span>🇵🇹 Portugal: +351.91.491.9296</span>
            <span>🇺🇸 USA: +1.510 944 4149</span>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-black text-white py-12">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center">
            <div className="flex items-center justify-center space-x-2 mb-6">
              <img 
                src="https://customer-assets.emergentagent.com/job_smart-ai-services-1/artifacts/7lqjkekm_mahoga_logo.jpg" 
                alt="Mahoga.ai Logo" 
                className="h-12 w-auto"
              />
            </div>
            <p className="text-gray-400 text-sm max-w-2xl mx-auto leading-relaxed">
              Transforming enterprises through intelligent SAP and AI integration. 
              From rapid prototyping to full-scale deployment, we deliver solutions that matter.
            </p>
            <div className="mt-8 pt-8 border-t border-gray-800 text-xs text-gray-500">
              © 2025 Mahoga.ai. All rights reserved.
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;